import React, { useState } from 'react';
import { Toolbar, Button, Menu, MenuItem, Box } from '@mui/material';
import {
  StyledAppBar,
  HeaderContent,
  LogoContainer,
  AppNameContainer,
  AppName,
  Tagline,
} from './Header.styles';
import Logo from './Logo';
import { useAuth } from 'contexts/AuthContext';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { TextField } from '@sitecore-jss/sitecore-jss-nextjs';

interface HeaderFields {
  HeaderTitle?: TextField;
  HeaderSummary?: TextField;
  LogoutLink?: {
    value: {
      href?: string;
      text?: string;
    };
  };
}

interface HeaderProps {
  fields?: HeaderFields;
  appBarBackgroundColor?: string;
}

const Header: React.FC<HeaderProps> = ({ fields, appBarBackgroundColor }) => {
  const { userName, isAuthenticated } = useAuth();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Get values from Sitecore fields (no fallbacks - handle empty gracefully)
  const headerTitle = fields?.HeaderTitle?.value;
  const headerSummary = fields?.HeaderSummary?.value;
  const logoutHref = fields?.LogoutLink?.value?.href || '/logout'; // Keep fallback for URL
  const logoutText = fields?.LogoutLink?.value?.text;

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    // Redirect to logout page (from Sitecore or default)
    window.location.href = logoutHref;
  };

  return (
    <StyledAppBar
      position="static"
      sx={appBarBackgroundColor ? { backgroundColor: appBarBackgroundColor } : undefined}
    >
      <Toolbar disableGutters sx={{ minHeight: '64px !important' }}>
        <HeaderContent>
          <LogoContainer>
            <Logo size={40} color="#FFFFFF" />
          </LogoContainer>
          <AppNameContainer>
            {headerTitle && <AppName>{headerTitle}</AppName>}
            {headerSummary && <Tagline>{headerSummary}</Tagline>}
          </AppNameContainer>
        </HeaderContent>

        {/* User Menu - Only show when authenticated */}
        {isAuthenticated && userName && (
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleUserMenuOpen}
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 24, ml: -1 }} />}
              sx={{
                color: '#FFFFFF',
                fontWeight: 600,
                textTransform: 'none',
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                fontSize: '18px',
                '& .MuiButton-endIcon': {
                  marginLeft: '4px',
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <PersonIcon sx={{ fontSize: 26 }} />
              {userName}
            </Button>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  borderRadius: 2,
                },
              }}
            >
              {logoutText && (
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(37, 94, 94, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LogoutIcon
                      sx={{
                        fontSize: 26,
                        color: '#255E5E',
                      }}
                    />
                    <Box
                      component="span"
                      sx={{
                        color: '#212121',
                        fontWeight: 500,
                        fontSize: '17px',
                      }}
                    >
                      {logoutText}
                    </Box>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </Box>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
